import { buildStructureTimeline, type ApiWorkoutStructure } from '../../utils/workoutStructure';
import React, { useId, useMemo } from 'react';
import type { StreamPoint } from '../../utils/workoutStream';

import { localizeWorkoutBuilderText } from '../../utils/localizeWorkoutBuilderText';

interface Props {
  stream: StreamPoint[];
  structure?: ApiWorkoutStructure;
  plannedDuration?: number;
  actualDuration?: number;
  label: string;
  kind: 'pace' | 'power' | 'heartRate' | 'speed' | 'altitude';
}

const WIDTH = 180;
const HEIGHT = 76;
const PAD = 6;

export function NotificationWorkoutSparkline({ stream, label, kind, structure, plannedDuration, actualDuration }: Props) {
  const gradientId = useId().replace(/:/g, '');
  const segments = useMemo(() => buildStructureTimeline(structure, plannedDuration), [structure, plannedDuration]);
  const values = useMemo(() => {
    const source = stream
      .map((point) => ({ time: point.timeSeconds, value: kind === 'pace' ? (point.speed != null && point.speed > 0 ? 60 / point.speed : null) : point[kind] }))
      .filter((point): point is { time: number; value: number } => point.value != null && Number.isFinite(point.value) && point.value >= 0 && Number.isFinite(point.time));
    if (source.length <= 72) return source;
    const bucket = source.length / 72;
    return Array.from({ length: 72 }, (_, index) => {
      const start = Math.floor(index * bucket);
      const end = Math.max(start + 1, Math.floor((index + 1) * bucket));
      const slice = source.slice(start, end);
      return { time: slice.reduce((sum, point) => sum + point.time, 0) / slice.length, value: slice.reduce((sum, point) => sum + point.value, 0) / slice.length };
    });
  }, [kind, stream]);

  if (values.length < 2) return null;
  const min = Math.min(...values.map(p => p.value));
  const max = Math.max(...values.map(p => p.value));
  const span = Math.max(1, max - min);
  const timeEnd = Math.max(1, actualDuration ?? 0, ...stream.map(p => p.timeSeconds), ...segments.map(p => p.endSec));
  const targetMax = Math.max(100, ...segments.map(p => p.maxPct));
  const points = values.map(({ value, time }) => {
    const x = PAD + (time / timeEnd) * (WIDTH - PAD * 2);
    const y = PAD + (1 - (value - min) / span) * (HEIGHT - PAD * 2);
    return [x, y] as const;
  });
  const linePath = points.map(([x, y], index) => `${index ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const fillPath = `${linePath} L${WIDTH - PAD},${HEIGHT - PAD} L${PAD},${HEIGHT - PAD} Z`;
  const color = kind === 'pace' ? '#32b9c7' : kind === 'power' ? 'var(--chart-primary, #163366)' : kind === 'heartRate' ? '#ff5f6d' : kind === 'speed' ? '#32b9c7' : '#8b7cf6';
  const unit = kind === 'pace' ? 'min/km' : kind === 'power' ? 'W' : kind === 'heartRate' ? '♥' : kind === 'speed' ? 'km/h' : 'm';

  return (
    <div className={`notification-workout-summary__sparkline${segments.length ? ' notification-workout-summary__sparkline--structured' : ''}`} aria-label={label} role="img">
      <span>{localizeWorkoutBuilderText(unit)}</span>
      {segments.length > 0 && <span className="notification-overlay-percent" aria-hidden>٪</span>}
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" aria-hidden>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={color} stopOpacity=".12" />
            <stop offset="1" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {segments.map((segment, index) => {
          const x = PAD + segment.startSec / timeEnd * (WIDTH - PAD * 2);
          const endX = PAD + segment.endSec / timeEnd * (WIDTH - PAD * 2);
          const y1 = HEIGHT - PAD - (segment.minPct / targetMax) * (HEIGHT - PAD * 2);
          const y2 = HEIGHT - PAD - (segment.maxPct / targetMax) * (HEIGHT - PAD * 2);
          return <path key={index} d={`M${x},${HEIGHT-PAD} L${x},${segment.isRamp ? y1 : (y1+y2)/2} L${endX},${segment.isRamp ? y2 : (y1+y2)/2} L${endX},${HEIGHT-PAD} Z`} className="notification-planned-overlay" fill="var(--chart-primary, #163366)" opacity=".12" />;
        })}
        {!segments.length && <path d={fillPath} fill={`url(#${gradientId})`} />}
        <path d={linePath} fill="none" stroke={color} strokeWidth="1.25" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}
